// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@keller/App/Components/Notify";
import { getIsCreditHold } from "@keller/App/Store/Selectors/GeneralSelectors";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        isCreditHold: getIsCreditHold(cart?.properties),
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({ cart, isCreditHold }: Props) => {
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    const isAwaitingApproval = cart.isAwaitingApproval;
    const cartNotPriced = cart.cartNotPriced;
    // XNG-Changes: add cart.cartLines?.some to condition
    const hasInsufficientInventory =
        (cart.hasInsufficientInventory ||
            cart.cartLines?.some(
                cartLine => cartLine.availability?.messageType === AvailabilityMessageType.LowStock,
            )) &&
        cart.fulfillmentMethod === FulfillmentMethod.Ship;
    const hasInvalidPrice = hasProductsWithInvalidPrice(cart);
    const failedToGetRealTimeInventory = cart.failedToGetRealTimeInventory;
    const productsCannotBePurchased = cart.cartLines?.some(o => o.isRestricted || !o.isActive);

    // XNG-Changes
    const hasInsufficientPickupInventory =
        cart.cartLines?.some(cartLine => cartLine.availability?.messageType === AvailabilityMessageType.OutOfStock) &&
        cart.fulfillmentMethod === FulfillmentMethod.PickUp;

    return (
        <StyledWrapper {...styles.approvingWrapper}>
            {/* <Typography {...styles.approvingText} data-test-selector="cart_approvingOrderMessage">
                {translate("You are approving order #{0}", cart.orderNumber)} 
            </Typography> */}
            {isAwaitingApproval && (
                <Notify type="info" data-test-selector="cart_approvingOrderMessage">
                    {translate("You are approving order #{0}", cart.orderNumber)}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify type="warning">{siteMessage("Cart_ProductsCannotBePurchased")}</Notify>
            )}
            {cartNotPriced && <Notify type="warning">{siteMessage("Cart_NoPriceAvailableAtCheckout")}</Notify>}
            {hasInsufficientInventory && (
                <Notify type="warning">{siteMessage("Cart_InsufficientInventoryAtCheckout")}</Notify>
            )}
            {hasInsufficientPickupInventory && (
                <Notify type="danger">{siteMessage("Keller_Cart_InsufficientPickupInventory")}</Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify type="warning">{siteMessage("Cart_InvalidPriceAtCheckout")}</Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify type="warning">{siteMessage("Cart_NoInventoryAvailableAtCheckout")}</Notify>
            )}
            {isCreditHold && <Notify type="warning">{siteMessage("Keller_CreditHoldMessage")}</Notify>}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
